import { createStore } from 'vuex';
import {
    ADD_ERRORS,
    DELETE_ERROR,
    SET_PROGRESS,
    SET_CONFIG,
    SET_ERRORS,
    SET_FIELD,
    SET_FORM_DATA,
    SET_LOCK,
    SET_SELECT_STATE,
    SET_STEP,
    SET_VALID_FIELD,
    SET_VALID_FIELDS,
    SET_DARK_BACKGROUND,
    SET_GLOBAL_ERROR,
    SET_RESPONSE,
    LOAD_TMSC,
    LOAD_QUERY,
    SET_ROUTE,
    SET_ANALYTICS_READY,
    INCREMENT_UPLOAD_DOCUMENT_COUNT,
} from './constants/mutation-types';
import { CallbackService } from '@/services/CallbackService';
import {
    AjaxActionInterface,
    AppConfig,
    BluemediaGateway,
    FieldInterface,
    Response,
    ResponseError,
    ValidFieldInterface,
} from '@/types';
import AjaxService from '@/services/AjaxService';
import { ErrorHelper } from '@/helpers/ErrorHelper';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import { Locale } from '@/constants/locale';
import { Action, ResponseStatus } from '@/constants/ajax';
import { userInfo } from '@/constants/user-information';
import { CountryHelper } from '@/helpers/CountryHelper';
import { ServerResponse } from '@/models/ServerResponse';
import { NavigationService } from '@/services/NavigationService';
import { DataLayerService } from '@/services/DataLayerService';
import { AnalyticsService } from '@/services/AnalyticsService';
import i18n, { loadLocaleMessagesAsync } from '@/i18n';
import { AdjustService } from '@/services/AdjustService';
import { UrlHelper } from '@/helpers/UrlHelper';
import { CookieHelper } from '@/helpers/CookieHelper';
import router from '@/router';
import * as amplitude from '@amplitude/analytics-browser';
import { staticDataLayers } from './constants/data-layers';
import { AuthZeroHelper } from './helpers/AuthZeroHelper';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
    amplitudeSetEmail,
    amplitudeSetResidence,
} from './utils/AmplitudeAnalyticsEvents';
import { optimoveIdentifyEvent, optimoveTrackEvent } from './utils/Optimove';

export default createStore({
    state: {
        previousStep: null as string | null,
        currentStep: 'newAccount' as string,
        timeToFinish: 8 as number,
        config: {
            version: undefined,
            taxOffices: [],
            citizenship: [],
            countries: [],
            phonePrefixesList: [],
            europeanCountries: [],
            prohibitedCountries: [],
            prohibitedCitizenship: [],
            bluemediaGatewayList: [] as BluemediaGateway[],
            tinCountries: [],
        } as AppConfig,
        ajaxService: undefined as unknown as AjaxService,
        navigationService: undefined as unknown as NavigationService,
        dataLayerService: undefined as unknown as DataLayerService,
        analyticsService: undefined as unknown as AnalyticsService,
        adjustService: undefined as unknown as AdjustService,
        // <editor-fold desc="Error handling">
        errors: new Map() as Map<string, string>,
        isErrorsChanged: false,
        validFields: new Set() as Set<string>,
        isValidFieldsChanged: false,
        globalError: null as null | string,
        // </editor-fold>
        isMobile: false,
        isSelectOpen: null as boolean | null,
        isInitComplete: false,
        isLocked: true,
        isDarkBackground: false,
        appVersion: process.env.NODE_ENV,
        isDetectedMobileDevice: UtilityHelper.isDetectedMobileDevice(),
        formData: {} as Record<string, any>,
        userInformation: {} as Record<string, any>,
        response: null as Response | null,
        tmsc: {} as Record<string, any>,
        query: {} as Record<string, any>,
        consents: [],
        isPhotoInstructionsOpen: false,
        isLoaderText: false,
        route: null,
        isLoadingThumbnail: false,
        AdjustUrlParams: '',
        residenceCountry: '',
        showLongDisclaimer: true,
        isGbg: false,
        showInstructionPopup: false,
        showSecurityPopup: false,
        isUploadDocumentStep: false,
        showInstructions: false,
        clientIdData: [] as any[],
        isSubheaderVisible: false,
        gbgToken: '',
        journeyId: '',
        analyticsReady: false,
        uploadDocumentCount: 0,
        isInternalDomain: false,
        internalDomains: ['tms.pl', 'direktpoint.pl', 'oanda.com'],
    },
    mutations: {
        [SET_SELECT_STATE](state, value: boolean) {
            state.isSelectOpen = value;
        },

        [SET_LOCK](state, value: boolean) {
            state.isLocked = value;
        },

        [SET_FIELD](state, field: FieldInterface) {
            (state as Record<string, any>)[field.key] = field.value;
        },

        [SET_CONFIG](state, field: FieldInterface) {
            (state.config as Record<string, any>)[field.key] = field.value;
        },

        [SET_STEP](state, step: string) {
            state.previousStep = state.currentStep;
            state.currentStep = step;
        },

        // <editor-fold desc="Form data">
        [SET_FORM_DATA](state, formData) {
            state.formData = formData;
        },
        // </editor-fold>

        // <editor-fold desc="Errors">
        [SET_ERRORS](state, errors: ResponseError[]) {
            const errorMap = new Map<string, string>();

            errors.forEach((error) => {
                errorMap.set(error.field, error.message);
            });

            state.errors = errorMap;
            state.isErrorsChanged = !state.isErrorsChanged;
        },

        [ADD_ERRORS](state, errors: ResponseError[]) {
            const errorMap = state.errors;

            errors.forEach((error) => {
                errorMap.set(error.field, error.message);
            });

            state.errors = errorMap;
            state.isErrorsChanged = !state.isErrorsChanged;
        },

        [DELETE_ERROR](state, errorName: string) {
            state.errors.delete(errorName);
            state.isErrorsChanged = !state.isErrorsChanged;
        },

        [SET_VALID_FIELD](state, data: ValidFieldInterface) {
            if (data.isValid) {
                state.validFields.add(data.name);
            } else {
                state.validFields.delete(data.name);
            }

            state.isValidFieldsChanged = !state.isValidFieldsChanged;
        },
        // </editor-fold>
        [SET_VALID_FIELDS](state, fields: ValidFieldInterface[]) {
            fields.forEach((data) => {
                if (data.isValid) {
                    state.validFields.add(data.name);
                } else {
                    state.validFields.delete(data.name);
                }
            });
            state.isValidFieldsChanged = !state.isValidFieldsChanged;
        },
        [SET_DARK_BACKGROUND](state, value: boolean) {
            state.isDarkBackground = value;
        },

        [SET_PROGRESS](state, value: number) {
            state.timeToFinish = value;
        },

        [SET_GLOBAL_ERROR](state, value: string | null) {
            state.globalError = value;
        },
        [SET_RESPONSE](state, value: Response) {
            state.response = value;
        },
        [LOAD_TMSC](state, tmscData) {
            const tmsc = { ...state.tmsc, ...tmscData };

            for (const key in tmsc) {
                if (undefined === tmsc[key]) {
                    delete tmsc[key];
                }
            }

            state.tmsc = tmsc;

            const cookieDomain = UrlHelper.getCookiesDomainUrl();
            CookieHelper.save(
                'tmsc',
                encodeURIComponent(JSON.stringify(state.tmsc)),
                3600 * 24 * 30,
                cookieDomain
            );
        },
        [LOAD_QUERY](state) {
            state.query = router.currentRoute.value.query;
        },

        [SET_ROUTE](state, route: any) {
            state.route = route;
        },
        [SET_ANALYTICS_READY](state, value: boolean) {
            state.analyticsReady = value;
        },
        [INCREMENT_UPLOAD_DOCUMENT_COUNT](state) {
            state.uploadDocumentCount += 1;
        },
    },
    actions: {
        async init({ commit, state }) {
            if (!state.isInitComplete) {
                try {
                    router.isReady().then(() => {
                        commit(SET_STEP, router.currentRoute.value.name);
                    });

                    state.adjustService = new AdjustService();
                    state.config.version =
                        i18n.global.locale.value === Locale.PL ? 'pl' : 'eu';
                    state.isMobile = UtilityHelper.isRouteMobile();
                    state.dataLayerService = new DataLayerService(this);
                    state.analyticsService = new AnalyticsService();
                    state.ajaxService = new AjaxService(state);
                    state.navigationService = new NavigationService(
                        state.dataLayerService,
                        state.analyticsService,
                        state.adjustService,
                        state.ajaxService,
                        commit,
                        this
                    );

                    commit(LOAD_QUERY);

                    const analyticsInitPromise = (async () => {
                        const token = state.ajaxService.getToken();

                        if (token) {
                            try {
                                const response: any =
                                    await state.ajaxService.verifyToken(token);

                                if (response.analytics) {
                                    state.analyticsService.setData({
                                        transaction_id:
                                            response.analytics.leadId,
                                        transaction_id_sha:
                                            response.analytics.leadIdSha,
                                        userId: response.analytics.userId,
                                        userIdSha: response.analytics.userIdSha,
                                    });

                                    commit(
                                        'LOAD_TMSC',
                                        response.analytics.tmsc
                                    );
                                }
                            } catch (err) {
                                commit(SET_STEP, 'restart');
                                state.navigationService.goToRoute('restart');
                                return;
                            } finally {
                                commit('SET_ANALYTICS_READY', true);
                            }
                        } else {
                            commit('SET_ANALYTICS_READY', true);
                        }
                    })();

                    const dictionariesRequest = state.ajaxService.runAction(
                        Action.DICTIONARIES,
                        {}
                    );
                    const translationRequest = loadLocaleMessagesAsync(
                        i18n.global.locale.value
                    );

                    Promise.all([
                        dictionariesRequest,
                        translationRequest,
                        analyticsInitPromise,
                    ]).then((values) => {
                        const response = values[0] as Record<string, any>;

                        if (ResponseStatus.ERROR !== response.status) {
                            commit(SET_CONFIG, {
                                key: 'taxOffices',
                                value: Object.entries(response.taxOfficeList),
                            });

                            const citizenshipList = Object.entries(
                                response.citizenship
                            ).map((entry) => {
                                return {
                                    key: entry[0],
                                    value: entry[1],
                                };
                            });

                            commit(SET_CONFIG, {
                                key: 'citizenship',
                                value: CountryHelper.orderCountries(
                                    citizenshipList
                                ),
                            });
                            const countries: FieldInterface[] = Object.entries(
                                response.countries
                            ).map((entry) => {
                                return {
                                    key: entry[0],
                                    value: entry[1],
                                };
                            });

                            commit(SET_CONFIG, {
                                key: 'countries',
                                value: CountryHelper.orderCountries(countries),
                            });

                            commit(SET_CONFIG, {
                                key: 'phonePrefixesList',
                                value: response.phonePrefixesList,
                            });

                            commit(SET_CONFIG, {
                                key: 'europeanCountries',
                                value: response.europeanCountries,
                            });
                            commit(SET_CONFIG, {
                                key: 'prohibitedCountries',
                                value: response.prohibitedCountries,
                            });
                            commit(SET_CONFIG, {
                                key: 'prohibitedCitizenship',
                                value: response.prohibitedCitizenship,
                            });
                            if (response.blueMediaGatewayList) {
                                commit(SET_CONFIG, {
                                    key: 'bluemediaGatewayList',
                                    value: Object.values(
                                        response.blueMediaGatewayList
                                    ),
                                });
                            }
                            if (response.blueMediaGatewayList) {
                                commit(SET_CONFIG, {
                                    key: 'tinCountries',
                                    value: Object.values(response.tinCountries),
                                });
                            }
                            if (response._ip) {
                                window.localStorage.setItem(
                                    'visitor_ip',
                                    response._ip
                                );
                            }
                        }

                        state.isInitComplete = true;
                        commit(SET_LOCK, false);
                    });
                } catch (e) {
                    console.error(e);
                    commit(SET_LOCK, false);
                }
            }
        },

        async validate({ commit, state }, model) {
            const isSingle =
                model &&
                typeof model === 'object' &&
                'name' in model &&
                'value' in model;

            const response = isSingle
                ? await state.ajaxService.validate(
                      (model as any).name,
                      (model as any).value
                  )
                : ((await state.ajaxService.validate(model)) as any);

            if (ResponseStatus.CANCELLED === response.status) return;

            if (isSingle) {
                const name = (model as any).name as string;

                const isValid: ValidFieldInterface = {
                    name,
                    isValid: response.status === ResponseStatus.SUCCESS,
                };

                commit(SET_VALID_FIELD, isValid);

                if (response.errors) {
                    const errors = Object.entries(response.errors).map(
                        ([field, messages]) =>
                            ({
                                field,
                                message: (messages as any)[0],
                            }) as ResponseError
                    );
                    commit(ADD_ERRORS, errors);
                }

                return;
            }

            const fields = Object.keys(model || {});
            const errorsObj = response.errors || {};

            const validList: ValidFieldInterface[] = fields.map((field) => ({
                name: field,
                isValid: !errorsObj[field],
            }));

            commit(SET_VALID_FIELDS, validList);

            if (response.errors) {
                const errors = Object.entries(response.errors).map(
                    ([field, messages]) =>
                        ({
                            field,
                            message: (messages as any)[0],
                        }) as ResponseError
                );
                commit(ADD_ERRORS, errors);
            }
        },

        async ajax({ commit, state }, payload: AjaxActionInterface) {
            await UtilityHelper.wait(() => state.isInitComplete);

            if (!state.isLocked) {
                commit(SET_LOCK, true);

                if (payload.data !== null) {
                    const payloadData = payload.data as Record<string, any>;
                    if (state.tmsc) {
                        payloadData['tmsc'] = state.tmsc;
                    }
                    if (state.query) {
                        const query = state.query as Record<string, any>;
                        payloadData['source'] = query['from'];
                    }
                }

                if (
                    state.route === 'desktopDocumentUpload' ||
                    state.route === 'desktopDocumentUploadBack' ||
                    state.route === 'mobileDocumentUpload' ||
                    state.route === 'mobileDocumentUploadBack'
                ) {
                    state.isLoaderText = true;
                    state.isLoadingThumbnail = true;
                }

                commit(SET_FORM_DATA, payload.data);

                if (state.formData) {
                    const formData = state.formData as Record<string, any>;
                    state.analyticsService.setData({
                        language_version: UtilityHelper.getLocale(),
                        verification_method: formData.dataUploadChoice
                            ? formData.dataUploadChoice
                            : undefined,
                        residency: (() => {
                            let result = '';
                            if (formData.residenceCountry) {
                                result += formData.residenceCountry;
                            }
                            return result.length > 0 ? result : undefined;
                        })(),
                        currency: formData.currencyChoice
                            ? formData.currencyChoice
                            : undefined,
                        us_taxpayer: formData.usaTaxPayer
                            ? formData.usaTaxPayer
                            : undefined,
                        politically_exposed: formData.pepDeclaration
                            ? formData.pepDeclaration
                            : undefined,
                        data_user: formData.dataUserDeclaration
                            ? formData.dataUserDeclaration
                            : undefined,
                    });
                }

                if (!state.config.bluemediaGatewayList) {
                    state.ajaxService
                        .runAction(Action.DICTIONARIES, {})
                        .then((response: any) => {
                            if (response.blueMediaGatewayList) {
                                commit(SET_CONFIG, {
                                    key: 'bluemediaGatewayList',
                                    value: Object.values(
                                        response.blueMediaGatewayList
                                    ),
                                });
                            }
                        });
                }
                // </editor-fold>

                const container = document.querySelector('#app > .container');

                try {
                    const response: ServerResponse =
                        await state.ajaxService.runAction(
                            payload.action,
                            state.formData,
                            payload.headers
                        );

                    let errors: ResponseError[] = [];

                    if (response.errors) {
                        errors = ErrorHelper.mapResponseErrors(response.errors);
                    }

                    if (response.dataContent) {
                        state.consents = response.dataContent.consents
                            ? response.dataContent.consents
                            : [];
                    }

                    commit(SET_ERRORS, errors);

                    if (response.callback) {
                        CallbackService.call(response.callback, response.args);
                    }

                    if (response.restart) {
                        state.formData = {};
                        commit(SET_STEP, 'restart');
                        await state.navigationService.goToRoute('restart');
                    } else {
                        if (
                            response.next &&
                            state.currentStep !== response.next
                        ) {
                            if (
                                (payload.action === Action.NEW_ACCOUNT &&
                                    response.next !== 'newLogin') ||
                                (payload.action === Action.LOGIN &&
                                    response.next &&
                                    response.next !== 'newLogin' &&
                                    response.analytics?.leadId)
                            ) {
                                const isLoginAction =
                                    payload.action === Action.LOGIN;
                                const email = isLoginAction
                                    ? (state.formData as any)?.loginEmail
                                    : (state.formData as any)?.email;
                                const password = isLoginAction
                                    ? (state.formData as any)?.loginPassword
                                    : (state.formData as any)?.password;

                                state.analyticsService.sendStepEvent(
                                    'dataUploadChoice'
                                );
                                state.dataLayerService.pushCustom(
                                    staticDataLayers.start
                                );

                                if (state.formData) {
                                    await optimoveIdentifyEvent(response);
                                    await optimoveTrackEvent(response);

                                    if (email && password) {
                                        const authZero = new AuthZeroHelper();
                                        authZero.auth0COAuthenticate(
                                            email,
                                            password
                                        );
                                    }
                                }

                                const getEmailDomain = (e?: string) =>
                                    e?.toLowerCase().trim().split('@')[1];
                                state.isInternalDomain =
                                    state.internalDomains.includes(
                                        getEmailDomain(email) ?? ''
                                    ) ||
                                    state.internalDomains.includes(
                                        getEmailDomain(
                                            response.info?.['Email']
                                        ) ?? ''
                                    );

                                const clientId = response.info?.['Client Id'];
                                if (clientId) {
                                    amplitude.setUserId(clientId);
                                    amplitudeSetEmail(email);
                                    if (state.residenceCountry) {
                                        amplitudeSetResidence(
                                            state.residenceCountry
                                        );
                                    }

                                    if (payload.action === Action.NEW_ACCOUNT) {
                                        amplitudeSendEvent(
                                            AmplitudeAnalyticsEvents.newAccount
                                                .ACCOUNT_CREATED
                                        );
                                    } else if (
                                        payload.action === Action.LOGIN
                                    ) {
                                        amplitudeSendEvent(
                                            AmplitudeAnalyticsEvents.newLogin
                                                .LOGIN_SUCCESS
                                        );
                                    }
                                }
                            }

                            state.dataLayerService.pushEntry(state.currentStep);
                            commit(SET_FORM_DATA, response.dataContent || {});
                            commit(SET_STEP, response.next);

                            for (const singleUserInfo in userInfo) {
                                if (
                                    response.dataContent &&
                                    singleUserInfo in response.dataContent
                                ) {
                                    state.userInformation = {
                                        [singleUserInfo]:
                                            response.dataContent[
                                                `${singleUserInfo}`
                                            ],
                                    };
                                }
                            }
                            await state.navigationService.goToRoute(
                                response.next as string
                            );
                        } else {
                            if (
                                !response.next &&
                                (state.currentStep ===
                                    'desktopDocumentUpload' ||
                                    state.currentStep ===
                                        'desktopDocumentUploadBack')
                            ) {
                                commit(
                                    SET_FORM_DATA,
                                    response.dataContent || {}
                                );
                            }
                        }
                    }

                    commit(SET_GLOBAL_ERROR, null);

                    UtilityHelper.wait(() => {
                        return container
                            ? container !==
                                  document.querySelector('#app > .container') ||
                                  !response.next ||
                                  response.next !== state.currentStep
                            : true;
                    }).then(() => {
                        commit(SET_LOCK, false);
                        state.isLoaderText = false;
                        state.isLoadingThumbnail = false;
                    });

                    commit(SET_RESPONSE, response);
                } catch (e: any) {
                    console.error(e);
                    commit(SET_GLOBAL_ERROR, e.message);
                    if (e.restart) {
                        commit(SET_STEP, 'restart');
                        state.ajaxService.restart();
                        await state.navigationService
                            .goToRoute('restart')
                            .catch((ex: any) => console.error(ex));
                    }

                    UtilityHelper.sleep(1000).then(() => {
                        commit(SET_LOCK, false);
                        state.isLoaderText = false;
                        state.isLoadingThumbnail = false;
                    });
                }
            } else {
                commit(SET_LOCK, false);
                state.isLoaderText = false;
                state.isLoadingThumbnail = false;
            }
        },
    },
    getters: {
        isErrorsChanged(state): boolean {
            return state.isErrorsChanged;
        },
        errors(state): Map<string, string> {
            return state.errors;
        },
        isValidFieldsChanged(state): boolean {
            return state.isValidFieldsChanged;
        },
        validFields(state): Set<string> {
            return state.validFields;
        },
        globalError(state): string | null {
            return state.globalError;
        },
    },
});
